import { useEffect, useRef } from "react";
import api from "../lib/api";

/**
 * Keeps the production backend pod warm by pinging /api/ping every N minutes
 * while the tab is visible. Prevents cold-start 404s on app.promotifyme.com.
 *
 * - Skips pings when the tab is hidden (saves bandwidth + battery).
 * - Fires one immediate ping on mount + when the tab becomes visible.
 * - Silent: failures are swallowed (the API interceptor already retries).
 */
const PING_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

export function KeepAlive() {
  const lastPingAt = useRef(0);

  useEffect(() => {
    const ping = async () => {
      if (document.hidden) return;
      const now = Date.now();
      if (now - lastPingAt.current < 30 * 1000) return; // throttle to 30s min
      lastPingAt.current = now;
      try {
        await api.get("/ping");
      } catch {
        /* the response interceptor already handles retries silently */
      }
    };

    // Initial ping on mount
    ping();

    const id = setInterval(ping, PING_INTERVAL_MS);
    const onVisibility = () => {
      if (!document.hidden) ping();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return null;
}

export default KeepAlive;
